"use client";

import { useTranslations } from "use-intl";

import { cn } from "@/lib/utils";
import type { AlbumType } from "@garimpa/lib/albumTypes";

interface MusicTypeChipProps {
  type: AlbumType | null | undefined;
  className?: string;
  fallbackToRelease?: boolean;
  tone?: "primary" | "secondary";
}

/** Unified chip for album/release classification labels (studio album, compilation, EP/single, etc.). */
export function MusicTypeChip({
  type,
  className,
  fallbackToRelease = false,
  tone = "primary",
}: MusicTypeChipProps) {
  const tAlbum = useTranslations("album");
  const tArtist = useTranslations("artist");
  const tSearchAlbumType = useTranslations("globals.search.albumType");

  if (!type && !fallbackToRelease) return null;

  let label = tAlbum("release");
  if (tone === "secondary" && type) {
    switch (type) {
      case "album":
        label = tSearchAlbumType("album");
        break;
      case "compilation":
        label = tSearchAlbumType("compilation");
        break;
      case "single_ep":
        label = tSearchAlbumType("single_ep");
        break;
      case "live":
        label = tSearchAlbumType("live");
        break;
      case "video":
        label = tSearchAlbumType("video");
        break;
      case "other":
        label = tSearchAlbumType("other");
        break;
    }
  } else {
    switch (type) {
      case "album":
        label = tAlbum("studioAlbum");
        break;
      case "compilation":
        label = tArtist("tabCompilations");
        break;
      case "single_ep":
        label = tArtist("tabSinglesEPs");
        break;
      case "live":
        label = tAlbum("liveAlbum");
        break;
      case "video":
        label = tAlbum("video");
        break;
      case "other":
        label = tSearchAlbumType("other");
        break;
    }
  }

  return (
    <span
      className={cn(
        "rounded border px-2 py-0.5 text-xs font-bold uppercase tracking-widest",
        tone === "secondary"
          ? "border-border/70 bg-secondary/40 text-muted-foreground"
          : "border-transparent bg-secondary text-foreground",
        className,
      )}
    >
      {label}
    </span>
  );
}
